import React, { useState, useEffect, useRef } from 'react';
// Logger removed - using inline conditions for production builds
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './WidgetConversationsChat.css';
import SpecialResponseRenderer, { hasSpecialResponseMarkers } from './SpecialResponseRenderer';
import {
  getWorkspaceToken,
  createWidgetThread,
  sendWidgetMessage,
} from '../../services/widgetChatApi';
import { TypingIndicator } from '../../ui';
import { PaperAirplaneIcon } from '@heroicons/react/24/solid';

const hasAccessibleContent = (children) => {
  if (children == null || children === false) return false;
  if (Array.isArray(children)) {
    return children.some((child) => child != null && child !== false && String(child).trim() !== '');
  }
  return String(children).trim() !== '';
};

const createMarkdownHeading = (Tag, className) => function MarkdownHeading({ children, ...props }) {
  if (!hasAccessibleContent(children)) {
    return <Tag className={className} aria-hidden="true" {...props} />;
  }
  return <Tag className={className} {...props}>{children}</Tag>;
};

const CHAT_SESSION_STORAGE_KEY = 'internal_widget_chat_session';

const Chat = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [widgetToken, setWidgetToken] = useState(null);
  const [workspaceUnavailable, setWorkspaceUnavailable] = useState(false);
  const [isSessionHydrated, setIsSessionHydrated] = useState(false);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  useEffect(() => {
    try {
      const savedSession = localStorage.getItem(CHAT_SESSION_STORAGE_KEY);
      if (!savedSession) return;
      const parsed = JSON.parse(savedSession);
      if (parsed?.sessionId) {
        setSessionId(parsed.sessionId);
      }
      if (Array.isArray(parsed?.messages) && parsed.messages.length > 0) {
        setMessages(parsed.messages);
      }
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Failed to restore internal chat session:', error);
    } finally {
      setIsSessionHydrated(true);
    }
  }, []);

  useEffect(() => {
    if (!isSessionHydrated) return;

    try {
      localStorage.setItem(
        CHAT_SESSION_STORAGE_KEY,
        JSON.stringify({ sessionId, messages })
      );
    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Failed to persist internal chat session:', error);
    }
  }, [sessionId, messages, isSessionHydrated]);

  useEffect(() => {
    if (!isSessionHydrated) return;

    const token = localStorage.getItem('directus_token') || sessionStorage.getItem('directus_token');
    if (!token) {
      navigate('/signin');
      return;
    }

    // Get workspace token on mount
    const fetchWorkspaceToken = async () => {
      try {
        setWorkspaceUnavailable(false);
        const token = await getWorkspaceToken();
        setWidgetToken(token);
        
        // Do not auto-create session on mount.
        // Session/thread should be created only after user sends first message.
      } catch (error) {
        const noWorkspaceLinked = error?.message?.includes('No linked workspace found for current user');
        if (noWorkspaceLinked) {
          setWorkspaceUnavailable(true);
          setWidgetToken(null);
          return;
        }

        process.env.NODE_ENV === "development" && console.error('Error fetching workspace token:', error);
      }
    };

    fetchWorkspaceToken();
  }, [navigate, sessionId, isSessionHydrated]);

  const handleSendMessage = async () => {
    if (!input.trim() || !widgetToken || workspaceUnavailable) return;

    setLoading(true);

    try {
      let currentSessionId = sessionId;

      // Create session if we don't have one
      if (!currentSessionId) {
        const threadData = await createWidgetThread(widgetToken);
        if (threadData.session_id) {
          currentSessionId = threadData.session_id;
          setSessionId(currentSessionId);
          
          // Load history if available
          if (threadData.history && threadData.history.length > 0) {
            const formattedMessages = threadData.history.map(msg => ({
              text: msg.content,
              sender: msg.role === 'assistant' ? 'assistant' : 'user'
            }));
            setMessages(formattedMessages);
          }
        } else {
          throw new Error('Failed to create session');
        }
      }

      // Add user message to UI immediately
      const userMessage = input;
      setMessages(prevMessages => [...prevMessages, { text: userMessage, sender: 'user' }]);
      setInput('');

      // Send message using EmbeddableChat API
      const response = await sendWidgetMessage(widgetToken, currentSessionId, userMessage);

      // Update messages with response
      if (response.history && response.history.length > 0) {
        const formattedMessages = response.history.map(msg => ({
          text: msg.content,
          sender: msg.role === 'assistant' ? 'assistant' : 'user'
        }));
        setMessages(formattedMessages);
      } else if (response.response) {
        // Fallback if history is not available but response is
        setMessages(prevMessages => [...prevMessages, { 
          text: response.response, 
          sender: 'assistant' 
        }]);
      }

    } catch (error) {
      process.env.NODE_ENV === "development" && console.error('Error in chat process:', error);
      setMessages(prevMessages => [...prevMessages, { 
        text: "Sorry, I encountered an error. Please try again.", 
        sender: 'assistant' 
      }]);
    } finally {
      setLoading(false);
    }
  };

  const renderMessage = (message) => {
    if (hasSpecialResponseMarkers(message.text)) {
      return <SpecialResponseRenderer content={message.text} />;
    }

    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: createMarkdownHeading('h1', 'text-xl font-bold mt-4 mb-2'),
          h2: createMarkdownHeading('h2', 'text-lg font-bold mt-4 mb-2'),
          h3: createMarkdownHeading('h3', 'text-md font-bold mt-4 mb-2'),
          h4: createMarkdownHeading('h4', 'text-sm font-bold mt-3 mb-2'),
          h5: createMarkdownHeading('h5', 'text-sm font-semibold mt-3 mb-2'),
          h6: createMarkdownHeading('h6', 'text-sm font-medium mt-3 mb-2'),
          strong: ({ node, ...props }) => <strong className="font-bold" {...props} />,
          em: ({ node, ...props }) => <em className="italic" {...props} />,
          ul: ({ node, ...props }) => <ul className="list-disc list-inside my-2 pl-4" {...props} />,
          ol: ({ node, ...props }) => <ol className="list-decimal list-inside my-2 pl-4" {...props} />,
          li: ({ node, ...props }) => <li className="mb-1" {...props} />,
          table: ({ node, ...props }) => (
            <div className="overflow-x-auto my-4 w-full">
              <table className="min-w-full table-auto border-collapse border border-gray-300 bg-white rounded-lg" {...props} />
            </div>
          ),
          thead: ({ node, ...props }) => <thead className="bg-gray-50" {...props} />,
          tbody: ({ node, ...props }) => <tbody {...props} />,
          tr: ({ node, ...props }) => <tr className="border-b border-gray-200 hover:bg-gray-50" {...props} />,
          th: ({ node, ...props }) => (
            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 border border-gray-300" {...props} />
          ),
          td: ({ node, ...props }) => (
            <td className="px-4 py-3 text-sm text-gray-700 border border-gray-300 align-top" {...props} />
          ),
          a: ({ node, children, ...props }) => (
            <a className="text-blue-600 hover:text-blue-800 underline font-medium transition-colors duration-200" target="_blank" rel="noopener noreferrer" {...props}>
              {hasAccessibleContent(children) ? children : <span className="sr-only">{props.href || 'link'}</span>}
            </a>
          ),
          code: ({ node, ...props }) => (
            <code className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-sm font-mono" {...props} />
          ),
          blockquote: ({ node, ...props }) => (
            <blockquote className="border-l-4 border-gray-300 pl-4 italic my-4 text-gray-600" {...props} />
          ),
          p: ({ node, ...props }) => {
            const children = React.Children.map(props.children, child => {
              if (typeof child === 'string') {
                return child.split(/(【\d+:\d+†[^】]+】)/g).map((part, index) => {
                  if (part.match(/【\d+:\d+†[^】]+】/)) {
                    const source = part.slice(1, -1);
                    return (
                      <span
                        key={index}
                        className="text-primary-500 cursor-pointer hover:underline ml-1 text-xs"
                        title={source}
                      >
                        [Source]
                      </span>
                    );
                  }
                  return part;
                });
              }
              return child;
            });
            return <p className="mb-2" {...props}>{children}</p>;
          }
        }}
      >
        {message.text}
      </ReactMarkdown>
    );
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleTextareaChange = (e) => {
    setInput(e.target.value);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  return (
    <div className="widget-chat-shell">
      <div className="widget-chat-header">
        <span aria-hidden="true" />
      </div>

      <div className="widget-chat-messages">
        {workspaceUnavailable && (
          <div className="widget-chat-row widget-chat-row-assistant">
            <div className="widget-chat-bubble widget-chat-bubble-assistant">
              Workspace is not linked to this account yet. Complete onboarding (or use a non-admin workspace account) to use chat.
            </div>
          </div>
        )}

        {messages.map((message, index) => (
          <div
            key={index}
            className={`widget-chat-row ${message.sender === 'user' ? 'widget-chat-row-user' : 'widget-chat-row-assistant'}`}
          >
            <div className={`widget-chat-bubble ${message.sender === 'user' ? 'widget-chat-bubble-user' : 'widget-chat-bubble-assistant'}`}>
              {renderMessage(message)}
            </div>
          </div>
        ))}

        {loading && (
          <div className="widget-chat-row widget-chat-row-assistant">
            <div className="widget-chat-bubble widget-chat-bubble-assistant">
              <TypingIndicator />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="widget-chat-input">
        <div className="widget-chat-textarea-wrap">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyPress}
            className="widget-chat-textarea"
            placeholder={loading ? t('common.loading') : t('assistant.typeMessage')}
            rows={1}
          />
          <button
            onClick={handleSendMessage}
            className="widget-chat-send"
            disabled={loading || !input.trim() || workspaceUnavailable}
            aria-label="Send message"
          >
            <PaperAirplaneIcon className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Chat;
